import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Images, LocateFixed, PackageSearch, Ruler, Settings2, Sparkles, SplitSquareVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { analytics } from '@/services/analytics';
import { WORKSPACE_CANVAS_MODES, type WorkspaceCanvasMode } from '@/components/workspace/workspaceMode';
import { MATCHING_TABS, type MatchingTab } from '@/components/etsy/etsyWorkspaceFlow';

type EtsyWorkspaceNavProps = {
  readonly activeMode: WorkspaceCanvasMode;
  readonly activeMatchingTab?: MatchingTab | null;
  readonly beforeNavigate?: (target: { mode: Exclude<WorkspaceCanvasMode, null>; matchingTab?: MatchingTab }) => boolean | Promise<boolean>;
};

type EtsyWorkspaceNavItem = {
  readonly mode: Exclude<WorkspaceCanvasMode, null>;
  readonly matchingTab?: MatchingTab;
  readonly label: string;
  readonly icon: typeof Settings2;
};

const items: ReadonlyArray<EtsyWorkspaceNavItem> = [
  {
    mode: WORKSPACE_CANVAS_MODES.etsySetup,
    label: 'Setup',
    icon: Settings2,
  },
  {
    mode: WORKSPACE_CANVAS_MODES.etsyImport,
    label: 'Import',
    icon: PackageSearch,
  },
  {
    mode: WORKSPACE_CANVAS_MODES.etsyOrders,
    label: 'Inbox',
    icon: PackageSearch,
  },
  {
    mode: WORKSPACE_CANVAS_MODES.etsyMatching,
    matchingTab: MATCHING_TABS.variations,
    label: 'Variations',
    icon: SplitSquareVertical,
  },
  {
    mode: WORKSPACE_CANVAS_MODES.etsyMatching,
    matchingTab: MATCHING_TABS.papers,
    label: 'Papers',
    icon: Sparkles,
  },
  {
    mode: WORKSPACE_CANVAS_MODES.etsyMatching,
    matchingTab: MATCHING_TABS.sizes,
    label: 'Sizes',
    icon: Ruler,
  },
  {
    mode: WORKSPACE_CANVAS_MODES.etsyMatching,
    matchingTab: MATCHING_TABS.addresses,
    label: 'Addresses',
    icon: LocateFixed,
  },
  {
    mode: WORKSPACE_CANVAS_MODES.etsyMatching,
    matchingTab: MATCHING_TABS.images,
    label: 'Images',
    icon: Images,
  },
  {
    mode: WORKSPACE_CANVAS_MODES.etsyToProduction,
    label: 'To Production',
    icon: Box,
  },
] as const;

const EtsyWorkspaceNav = ({ activeMode, activeMatchingTab = null, beforeNavigate }: EtsyWorkspaceNavProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const normalizedActiveMode = useMemo<WorkspaceCanvasMode>(() => {
    if (
      activeMode === WORKSPACE_CANVAS_MODES.etsySetup
      || activeMode === WORKSPACE_CANVAS_MODES.etsyImport
      || activeMode === WORKSPACE_CANVAS_MODES.etsyOrders
      || activeMode === WORKSPACE_CANVAS_MODES.etsyMatching
      || activeMode === WORKSPACE_CANVAS_MODES.etsyToProduction
    ) {
      return activeMode;
    }
    return null;
  }, [activeMode]);

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border/60 bg-muted/20 p-2">
      {items.map((item) => {
        const Icon = item.icon;
        const isActive = item.mode === WORKSPACE_CANVAS_MODES.etsyMatching
          ? normalizedActiveMode === WORKSPACE_CANVAS_MODES.etsyMatching && activeMatchingTab === item.matchingTab
          : normalizedActiveMode === item.mode;
        return (
          <Button
            key={`${item.mode}-${item.matchingTab ?? 'base'}`}
            type="button"
            variant={isActive ? 'default' : 'outline'}
            size="sm"
            onClick={async () => {
              const shouldNavigate = beforeNavigate
                ? await beforeNavigate({ mode: item.mode, matchingTab: item.matchingTab })
                : true;
              if (!shouldNavigate) {
                return;
              }
              analytics.track('button: ui.etsy_workspace_nav_click', {
                mode: item.mode,
                matchingTab: item.matchingTab ?? null,
              });
              const next = new URLSearchParams();
              next.set('canvas', item.mode);
              if (item.matchingTab) {
                next.set('matchingTab', item.matchingTab);
              }
              navigate(`/studio?${next.toString()}`);
            }}
          >
            <Icon className="mr-2 h-4 w-4" />
            {item.mode === WORKSPACE_CANVAS_MODES.etsySetup
              ? t('integrations.etsy.nav.setup', 'Setup')
              : item.mode === WORKSPACE_CANVAS_MODES.etsyImport
                ? t('integrations.etsy.nav.import', 'Import')
                : item.mode === WORKSPACE_CANVAS_MODES.etsyOrders
                  ? t('integrations.etsy.nav.inbox', 'Inbox')
                  : item.mode === WORKSPACE_CANVAS_MODES.etsyToProduction
                    ? t('integrations.etsy.nav.toProduction', 'To Production')
                    : item.matchingTab === MATCHING_TABS.variations
                      ? t('integrations.etsy.matching.tabs.variations', 'Variations')
                      : item.matchingTab === MATCHING_TABS.papers
                        ? t('integrations.etsy.matching.tabs.papers', 'Papers')
                        : item.matchingTab === MATCHING_TABS.sizes
                          ? t('integrations.etsy.matching.tabs.sizes', 'Sizes')
                          : item.matchingTab === MATCHING_TABS.addresses
                            ? t('integrations.etsy.matching.tabs.addresses', 'Addresses')
                            : t('integrations.etsy.matching.tabs.images', 'Images')}
          </Button>
        );
      })}
    </div>
  );
};

export default EtsyWorkspaceNav;
